import { parseApiClientFullResponse } from '@framework/api-client';
import { t } from '@framework/i18n';
import { message } from 'antd';
import { AxiosRequestHeaders } from 'axios';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useDocumentsInlineViewerContext } from '../../ui/documents/DocumentsInlineViewerContext';
import { useFileLocalDownload } from './useFileLocalDownload';

export interface IParsedFile {
  data: any;
  filename: string;
  mimetype?: string;
  openInNewWindow?: boolean;
}

export interface IUseFileDownloadApiOptionsParams {
  /**
   * Nome del file da scaricare.
   * Se non viene specificato, il nome del file verrà recuperato dall'header
   */
  filename?: string | null;
  mimetype?: string;
  parseData?: (data: any) => any;
  textResponse?: boolean;
  /** È possibile fornire una funzione che presi gli header restituisca il nome del file da scaricare */
  getFilenameFromHeaders?: (headers?: AxiosRequestHeaders) => string;
  onError?: (error: any) => void;
  onSuccess?: (data: any) => void;
  /**  opzioni aggiuntive query */
  queryOptions?: Record<string, any>;
  /** Header aggiuntivi */
  headers?: Record<string, string>;
  /** Apre il file in una nuova finestra */
  openInNewWindow?: boolean;
  /** Flag per verificare se siamo in backoffice */
  showInlineViewer?: boolean;
}

/**
 * Prende il nome del file dall'header (content-disposition: 'inline; filename="nomefile.pdf"')
 */
export function getFilenameFromHeaders(headers?: AxiosRequestHeaders) {
  const chunks: string[] = headers?.['content-disposition']?.split(';') ?? [];
  const filenameChunk = chunks.find(chunk => chunk.includes('filename='));
  if (!filenameChunk) {
    return t('download-file.no-filename-from-header', 'Documento');
  }

  return filenameChunk.split('=')[1].replace(/"/g, '');
}

// Questa è una soluzione stabile, seppur temporanea, per evitare di
// scaricare più volte lo stesso file, per via degli `onSuccess`.
let downloadedAtLast = 0;

function onErrorDefault(e: any) {
  message.error(t("download-file.error-downloading-file", 'Si è verificato un problema durante il download del file')); // prettier-ignore
}

/**
 * Ritorna le opzioni per le query che scaricano i file
 */
export function useFileDownloadApiOptions(
  props: IUseFileDownloadApiOptionsParams,
) {
  const {
    filename,
    mimetype,
    parseData,
    textResponse = false,
    getFilenameFromHeaders: _getFilenameFromHeaders,
    onError,
    queryOptions,
    headers,
  } = props;
  const handleDownloadFile = useFileLocalDownload();
  const { handleSetCurrentDocument } = useDocumentsInlineViewerContext();
  const theme = useTheme();
  const result = useMemo(() => {
    return {
      options: {
        request: {
          fullResponse: true,
          responseType: textResponse ? undefined : ('arraybuffer' as const),
          headers: headers,
        },
        query: {
          keepPreviousData: true,
          enabled: false,
          cacheTime: 0,
          staleTime: Infinity,

          ...queryOptions,
        },
      },
      onSuccess: (data: any) => {
        if (Date.now() - downloadedAtLast < 500) return;
        downloadedAtLast = Date.now();

        const parsed = parseApiClientFullResponse(data);

        const parsedFile: IParsedFile = {
          data: parseData ? parseData(parsed.data) : parsed.data,
          filename:
            filename ??
            _getFilenameFromHeaders?.(parsed.headers) ??
            getFilenameFromHeaders(parsed.headers),
          mimetype: mimetype ?? parsed.headers?.getContentType()?.toString(),
          openInNewWindow: props.openInNewWindow,
        };

        // Se abilitato in backoffice e se il file è un PDF apre il drawer
        if (
          theme.enablePdfDrawer &&
          props.showInlineViewer &&
          parsedFile.mimetype === 'application/pdf'
        ) {
          handleSetCurrentDocument(parsedFile);
        } else {
          handleDownloadFile(parsedFile);
        }

        props.onSuccess?.(data);
      },
      onError: onError ?? onErrorDefault,
    };
  }, [
    _getFilenameFromHeaders,
    filename,
    handleDownloadFile,
    headers,
    mimetype,
    onError,
    parseData,
    props,
    queryOptions,
    textResponse,
    handleSetCurrentDocument,
  ]);

  return result;
}

export type FileDownloadAPiOptions = ReturnType<
  typeof useFileDownloadApiOptions
>['options'];

export type FileDownloadApiOptionsOnSuccess = ReturnType<
  typeof useFileDownloadApiOptions
>['onSuccess'];

export type FileDownloadApiOptionsOnError = ReturnType<
  typeof useFileDownloadApiOptions
>['onError'];
